import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Flag } from "lucide-react";

const REASONS = [
  { value: "illegal", label: "Illegal product or content" },
  { value: "counterfeit", label: "Counterfeit / fake item" },
  { value: "misleading", label: "Misleading description or price" },
  { value: "offensive", label: "Offensive content" },
  { value: "scam", label: "Suspected scam" },
  { value: "other", label: "Something else" },
];

export const ReportListingDialog = ({ productId }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) return toast.error("Pick a reason");
    setBusy(true);
    try {
      await api.post("/reports", { product_id: productId, reason, details: details || null });
      toast.success("Report received — our team will review this listing");
      setOpen(false);
      setReason("");
      setDetails("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="report-listing-btn"
          className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-[#FF3B30] transition-colors"
        >
          <Flag className="h-3.5 w-3.5" /> Report this listing
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report this listing</DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm">
            Tell us what's wrong. Reports are reviewed by the VISMARKS team (EU DSA notice-and-action).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            {REASONS.map((r) => (
              <label
                key={r.value}
                className={`flex items-center gap-2.5 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${
                  reason === r.value ? "border-[#FF3B30]/60 bg-[#FF3B30]/10 text-white" : "border-[#27272A] text-neutral-400 hover:border-[#3f3f46]"
                }`}
              >
                <input
                  type="radio"
                  name="report-reason"
                  data-testid={`report-reason-${r.value}`}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="accent-[#FF3B30]"
                />
                {r.label}
              </label>
            ))}
          </div>
          <Textarea
            data-testid="report-details-input"
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, 1000))}
            placeholder="Optional details (max 1000 characters)…"
            className="bg-[#0A0A0A] border-[#27272A] text-white min-h-[80px]"
          />
          <Button
            onClick={submit}
            disabled={busy || !reason}
            data-testid="report-submit-btn"
            className="w-full rounded-full h-10 bg-[#FF3B30] hover:bg-[#e0342a] text-white font-semibold"
          >
            {busy ? "Sending…" : "Send report"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
